import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  Divider,
  Toolbar,
  IconButton,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { pages as allPages } from "../../pages";

const findPageByPath = (path, pagesArray) => {
  const findPageRecursively = (pages, targetPath) => {
    for (const page of pages) {
      if (page?.path === targetPath) {
        return page;
      }
      if (page?.subPages && page?.subPages?.length > 0) {
        const foundPage = findPageRecursively(page.subPages, targetPath);
        if (foundPage) {
          return foundPage;
        }
      }
    }
    return null;
  };
  return findPageRecursively(pagesArray, path);
};

const isChildActive = (page, currentPage) => {
  if (page.subPages) {
    const currentPageObject = findPageByPath(currentPage, allPages);
    if (!currentPageObject) return false;

    for (const subPage of page.subPages) {
      if (subPage.pageId.some((id) => currentPageObject.pageId.includes(id))) {
        return true;
      }
      if (subPage?.subPages && isChildActive(subPage, currentPage)) {
        return true;
      }
    }
  }
  return false;
};

const SidebarItem = ({ page, currentPage, setPage, handleCloseSidebar }) => {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = page?.subPages && page?.subPages?.length > 0;
  const currentPageObject = findPageByPath(currentPage, allPages);

  const isSelected = page.pageId.some((id) =>
    currentPageObject?.pageId?.includes(id)
  );

  useEffect(() => {
    if (hasChildren && (isSelected || isChildActive(page, currentPage))) {
      setExpanded(true);
    }
  }, [currentPage, hasChildren, isSelected, page]);

  const handleToggle = (event) => {
    event.stopPropagation();
    setExpanded(!expanded);
  };

  const handleItemClick = () => {
    setPage(page?.path);
    if (!hasChildren) {
      handleCloseSidebar();
    }
  };

  const itemStyles = {
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: "#6e6e6e",
    },
    borderRadius: "8px",
    mx: "8px",
    my: "4px",
    ...(isSelected && { backgroundColor: "#6e6e6e" }),
  };

  return (
    <>
      <ListItemButton onClick={handleItemClick} sx={itemStyles}>
        <ListItemText primary={page.title} />
        {hasChildren && (
          <IconButton onClick={handleToggle} sx={{ color: "inherit", p: 0.5 }}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        )}
      </ListItemButton>

      {hasChildren && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {page?.subPages?.map((subPage, index) => (
              <Box key={subPage?.path || index} sx={{ pl: 4 }}>
                <SidebarItem
                  page={subPage}
                  currentPage={currentPage}
                  setPage={setPage}
                  handleCloseSidebar={handleCloseSidebar}
                />
              </Box>
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

const SidebarNavigation = ({
  currentPage,
  setPage,
  isSidebarOpen,
  setIsSidebarOpen,
  pages,
}) => {
  const handleCloseSidebar = () => {
    setIsSidebarOpen(false);
  };

  return (
    <Box
      sx={{
        width: 250,
        backgroundColor: "#434343",
        color: "#ecf0f1",
        position: "fixed",
        top: 64,
        left: isSidebarOpen ? 0 : -250,
        height: "calc(100vh - 64px)",
        zIndex: 1200,
        transition: "left 0.3s ease-in-out",
        boxShadow: "2px 0 5px rgba(0,0,0,0.5)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 2,
          paddingBottom: 0,
        }}
      >
        <Typography
          variant="p"
          noWrap
          component="p"
          sx={{
            color: "#ecf0f1",
            textDecoration: "none",
            letterSpacing: "0rem",
            mb: 1,
            fontWeight: "bold",
          }}
        >
          REPORTS
        </Typography>
        <Divider sx={{ backgroundColor: "#fff", width: "40%" }} />
      </Toolbar>

      <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
        <List component="nav">
          {pages?.map((page, index) => (
            <SidebarItem
              key={page?.path || index}
              page={page}
              currentPage={currentPage}
              setPage={setPage}
              handleCloseSidebar={handleCloseSidebar}
            />
          ))}
        </List>
      </Box>
    </Box>
  );
};

export default SidebarNavigation;
